import type { MessageKey } from '../../shared/i18n/messages';

/**
 * Turn a failed send-otp / confirm-otp call into the right message and, for a
 * rate-limit, the seconds to count down (D-70/D-71).
 *
 * Two rules the sheet used to break:
 *  - the SEND step and the CONFIRM step are different failures. "Confira o
 *    código" only makes sense after a code exists, so a send failure must never
 *    show it — there is nothing to check yet.
 *  - a 429 carries `retry_after_ms` and a `reason`; the sheet must surface the
 *    wait instead of a generic "try again". A 60 s cooldown is a countdown the
 *    button can run; the hourly/daily caps are too long to tick down, so those
 *    say "try later".
 *
 * Duck-typed rather than tied to `ApiError` so it stays a pure, testable
 * function: httpClient sets `.message` to the server `code`, `.status` to the
 * HTTP status and `.payload` to the JSON body.
 */
export type ContactOtpErrorInfo = { key: MessageKey; cooldownSec: number };

type HttpErrorLike = { status: number; code: string; payload: { reason?: string; retry_after_ms?: number } | null };

function asHttpError(error: unknown): HttpErrorLike | null {
  if (!error || typeof error !== 'object' || !('status' in error)) return null;
  const e = error as { status?: unknown; message?: unknown; code?: unknown; payload?: unknown };
  return {
    status: Number(e.status || 0),
    code: String(e.message || e.code || ''),
    payload: (e.payload as HttpErrorLike['payload']) ?? null,
  };
}

export function contactOtpError(error: unknown, phase: 'send' | 'confirm'): ContactOtpErrorInfo {
  const http = asHttpError(error);
  const code = http?.code || '';

  if (http && (http.status === 429 || code === 'otp_rate_limited')) {
    const reason = String(http.payload?.reason || '');
    const retryMs = Number(http.payload?.retry_after_ms || 0);
    if (reason === 'cooldown') {
      return { key: 'errOtpRateLimited', cooldownSec: retryMs > 0 ? Math.ceil(retryMs / 1000) : 60 };
    }
    return { key: 'verifyOtpTooManyToday', cooldownSec: 0 };
  }
  if (code === 'OTP_SMS_UNAVAILABLE') return { key: 'verifyOtpSmsUnavailable', cooldownSec: 0 };
  if (code === 'OTP_EMAIL_UNAVAILABLE') return { key: 'verifyOtpEmailUnavailable', cooldownSec: 0 };
  if (code === 'PHONE_TAKEN') return { key: 'verifyPhoneTaken', cooldownSec: 0 };
  if (code === 'EMAIL_TAKEN') return { key: 'verifyEmailTaken', cooldownSec: 0 };
  if (phase === 'confirm') {
    if (code === 'otp_expired') return { key: 'verifyOtpExpired', cooldownSec: 0 };
    return { key: 'verifyOtpInvalid', cooldownSec: 0 };
  }
  return { key: 'verifySendFailed', cooldownSec: 0 };
}
